import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { BrandMark } from '@/components/atoms'
import { GhostCleanupModal, type GhostProject, DraftResumeBanner } from '@/components/modals'
import { clearDraft, hasDraft, loadDraft, newDraft, type DraftPayload } from '@/lib/draft-store'
import { useAuthStore } from '@/stores/auth-store'
import { useIdeStore } from '@/features/ide/store'
import { apiFetch, ApiClientError } from '@/lib/api'
import type { CareerCategory, VibeProject, VibeUser } from '@vibe/shared-types'

export const Route = createFileRoute('/onboarding')({
  component: OnboardingPage,
})

const INTEREST_KEYWORDS = [
  '환경',
  '데이터',
  '의학',
  '생명',
  '음악',
  '미술',
  '심리',
  '경제',
  '문학',
  '역사',
  '교육',
  '복지',
  '진로미정',
]

interface CategoryOption {
  id: CareerCategory
  label: string
}

const CATEGORY_OPTIONS: CategoryOption[] = [
  { id: 'engineering_ai', label: '이공·AI' },
  { id: 'medicine_life', label: '의학·생명' },
  { id: 'humanities_social', label: '인문·사회' },
  { id: 'business', label: '경상·경영' },
  { id: 'arts_athletics', label: '예체능' },
  { id: 'education_welfare', label: '교육·복지' },
  { id: 'undecided', label: '진로 미정' },
]

interface RecProject {
  id: string
  title: string
  desc: string
  se: string
  icon: string
  isCached?: boolean
  isAvailable?: boolean
}

/**
 * 진로 카테고리별 추천 시드 프로젝트.
 * spec.md §15.2 의 7개 카테고리 × 3개씩. 실제 운영 콘텐츠(35~70개)는 Phase 2 에서 보강.
 */
const PROJECTS_BY_CATEGORY: Record<CareerCategory, RecProject[]> = {
  engineering_ai: [
    {
      id: 'air-quality-bot',
      title: '우리 동네 미세먼지 챗봇',
      desc: '환경부 공공 API로 우리 동네 PM2.5를 가져와서 챗봇처럼 알려주는 한 페이지 웹',
      se: '세특 키워드 · 환경 데이터 시각화, 공공 API 활용',
      icon: '🌫️',
    },
    {
      id: 'energy-tracker',
      title: '내 방 에너지 소비 추적',
      desc: '전기료 입력하면 월별 사용량 그래프로 보여주는 작은 가계부',
      se: '세특 키워드 · 시스템 사고, 정량 분석',
      icon: '⚡',
    },
    {
      id: 'cv-pet-mood',
      title: '강아지 표정 분류기',
      desc: '내가 찍은 강아지 사진을 표정별로 분류해보는 첫 머신러닝',
      se: '세특 키워드 · AI 입문, 데이터 라벨링',
      icon: '🐕',
    },
  ],
  medicine_life: [
    {
      id: 'symptom-checker',
      title: '증상 자가체크 도우미',
      desc: '증상을 적으면 일반적인 정보를 알려주는 챗봇. 진단은 하지 않아요.',
      se: '세특 키워드 · 의학·생명윤리, 시민 헬스리터러시',
      icon: '🩺',
    },
    {
      id: 'epidemiology-viz',
      title: '역학 데이터 시각화',
      desc: '질병관리청 공공 데이터로 시계열 그래프를 그려봐요',
      se: '세특 키워드 · 의학 통계, 자기주도 탐구',
      icon: '📊',
    },
    {
      id: 'medication-reminder',
      title: '복약 알림 캘린더',
      desc: '어르신 약 드실 시간을 알림으로 챙겨드리는 작은 도구',
      se: '세특 키워드 · 의학·복지 융합',
      icon: '💊',
    },
  ],
  humanities_social: [
    {
      id: 'classic-novel-bot',
      title: '고전소설 속 한 문단 챗봇',
      desc: '구운몽 본문을 문단별로 쪼개서, 질문에 맞는 문단을 골라 답해주는 챗봇',
      se: '세특 키워드 · 국어, RAG, 텍스트 검색',
      icon: '📖',
    },
    {
      id: 'history-map',
      title: '한국사 사건 인터랙티브 지도',
      desc: '시대별 주요 사건을 지도에 점으로 찍고, 클릭하면 설명이 나오는 한 페이지 웹',
      se: '세특 키워드 · 한국사, 시각적 자료 정리',
      icon: '🗺️',
    },
    {
      id: 'survey-portfolio',
      title: '학교 친구 인식 조사',
      desc: '간단한 설문 폼 + 결과 차트로 보여주는 미니 리서치',
      se: '세특 키워드 · 사회 조사, 통계 시각화',
      icon: '📋',
    },
  ],
  business: [
    {
      id: 'snack-ledger',
      title: '학교 매점 영수증 가계부',
      desc: '8주 동안 내 매점 소비를 카테고리별 도넛 차트로',
      se: '세특 키워드 · 경제 행동 관찰, 데이터 정리',
      icon: '🧾',
    },
    {
      id: 'mini-shop',
      title: '동아리 굿즈 미니 쇼핑몰',
      desc: '상품 카드 + 장바구니 + 카카오톡 주문 링크까지 한 페이지로',
      se: '세특 키워드 · 마케팅, UX 흐름 설계',
      icon: '🛍️',
    },
    {
      id: 'allowance-planner',
      title: '한 달 용돈 계획표',
      desc: '용돈 입력하면 카테고리별 예산을 자동으로 짜주는 도구',
      se: '세특 키워드 · 자기 관리, 의사결정',
      icon: '💰',
    },
  ],
  arts_athletics: [
    {
      id: 'portfolio-site',
      title: '내 작품 포트폴리오 사이트',
      desc: '그림·작곡·운동 영상 등을 카드로 정리해서 친구한테 공유',
      se: '세특 키워드 · 자기 표현, 큐레이션',
      icon: '🎨',
    },
    {
      id: 'music-viz',
      title: '내가 만든 음악 시각화',
      desc: '오디오 파일 올리면 파형·BPM을 화면에 그려주는 미니 비주얼라이저',
      se: '세특 키워드 · 음악 분석, 시각화',
      icon: '🎵',
    },
    {
      id: 'workout-log',
      title: '운동 기록장',
      desc: '오늘 한 운동·세트·횟수를 기록하고 주간 그래프로 보는 트래커',
      se: '세특 키워드 · 자기 관리, 데이터 누적',
      icon: '🏃',
    },
  ],
  education_welfare: [
    {
      id: 'peer-tutor',
      title: '또래 튜터 챗봇',
      desc: '내가 잘하는 과목 문제를 학생이 물어보면 단계별로 도와주는 챗봇',
      se: '세특 키워드 · 교육 설계, 학습 코칭',
      icon: '👩‍🏫',
    },
    {
      id: 'study-planner',
      title: '하루 공부 플래너',
      desc: '오늘 할 일을 입력하면 진행률·집중 시간을 자동으로 정리',
      se: '세특 키워드 · 자기주도 학습, 시간 관리',
      icon: '📅',
    },
    {
      id: 'volunteer-board',
      title: '동네 봉사 게시판',
      desc: '주변 봉사 활동 정보를 한 곳에 모아 친구들과 공유',
      se: '세특 키워드 · 지역 사회 참여, 정보 큐레이션',
      icon: '🤝',
    },
  ],
  undecided: [
    {
      id: 'mood-diary',
      title: '오늘의 기분 일기',
      desc: '하루 기분을 이모지로 찍고, 한 달 흐름을 차트로',
      se: '세특 키워드 · 자기 이해, 데이터 누적',
      icon: '📔',
    },
    {
      id: 'career-quiz',
      title: '나를 알아가는 진로 검사',
      desc: '간단한 10문항 검사로 내 관심 분야를 추천받기',
      se: '세특 키워드 · 자기 탐색, 진로 설계',
      icon: '🧭',
    },
    {
      id: 'todo-classic',
      title: '오늘 할 일 체크리스트',
      desc: '하고 싶은 거 적고, 끝낸 거 체크. 진행률까지',
      se: '세특 키워드 · 자기 관리 기본기',
      icon: '✅',
    },
  ],
}

const CATEGORY_DISPLAY: Record<CareerCategory, string> = {
  engineering_ai: '이공·AI',
  medicine_life: '의학·생명',
  humanities_social: '인문·사회',
  business: '경상·경영',
  arts_athletics: '예체능',
  education_welfare: '교육·복지',
  undecided: '진로 미정',
}

// plan v1.3 §7.2 — phase 가 'idle' 또는 'archived' 가 아닌 작품을 정리 후보로 분류.
// 학생이 "그대로 둘게요" 누르면 sessionStorage 에 마킹해서 같은 세션 동안 다시 묻지 않음.
const GHOST_KEEP_KEY = 'vibe:ghost-keep'
const GHOST_GRACE_MS = 6 * 60 * 60 * 1000 // 만든 지 6시간 이내인 작품은 진행 중일 수 있으므로 제외.
const FALLBACK_AVAILABLE_SEEDS = new Set(['symptom-checker', 'mood-diary', 'wind-feasibility'])

function isGhostCandidate(p: VibeProject & { phase?: string }): boolean {
  if (p.phase === 'idle' || p.phase === 'archived') return false
  // 만든 직후 작품은 ghost 가 아님 (학생이 방금 만든 것일 수 있음).
  if (p.createdAt && Date.now() - p.createdAt < GHOST_GRACE_MS) return false
  return true
}

function seedAvailable(p: RecProject): boolean {
  return p.isAvailable ?? FALLBACK_AVAILABLE_SEEDS.has(p.id)
}

function deriveAgeGroup(birthDate: string): 'unknown' | 'under14' | 'over14' {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(birthDate)) return 'unknown'
  const year = Number.parseInt(birthDate.slice(0, 4), 10)
  const month = Number.parseInt(birthDate.slice(5, 7), 10)
  const day = Number.parseInt(birthDate.slice(8, 10), 10)
  const today = new Date()
  let age = today.getFullYear() - year
  const monthDiff = today.getMonth() + 1 - month
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < day)) age--
  return age < 14 ? 'under14' : 'over14'
}

function needsAgeProfile(profile: VibeUser | null): boolean {
  if (!profile) return true
  if (profile.ageGroup === 'unknown') return true
  if (profile.ageGroup === 'over14' && !profile.birthDate && !profile.birthYear) return true
  return false
}

function guardianBlocked(profile: VibeUser | null): boolean {
  return profile?.ageGroup === 'under14' && !profile.guardianConsent?.verifiedAt
}

type SeedMetaResponse = {
  items: Array<{
    seedId: string
    title: string
    desc: string
    se: string
    icon: string
    category: CareerCategory
    isCached?: boolean
    isAvailable?: boolean
  }>
}

function OnboardingPage() {
  const navigate = useNavigate()
  const profile = useAuthStore((s) => s.profile)
  const authStatus = useAuthStore((s) => s.status)
  const setProfile = useAuthStore((s) => s.setProfile)
  const firebaseUser = useAuthStore((s) => s.firebaseUser)
  const setProjectInfo = useIdeStore((s) => s.setProjectInfo)
  const [step, setStep] = useState(0)
  const [interests, setInterests] = useState<string[]>([])
  const [category, setCategory] = useState<CareerCategory | null>(null)
  const [chosen, setChosen] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [input, setInput] = useState('')
  const [ghosts, setGhosts] = useState<GhostProject[]>([])
  const [ghostOpen, setGhostOpen] = useState(false)
  const [draftToResume, setDraftToResume] = useState<DraftPayload | null>(null)
  const [seedMap, setSeedMap] = useState<Record<CareerCategory, RecProject[]> | null>(null)
  const [birthDate, setBirthDate] = useState(profile?.birthDate ?? '')
  const [grade, setGrade] = useState(profile?.grade ?? '')
  const [school, setSchool] = useState(profile?.school ?? '')
  const [guardianEmail, setGuardianEmail] = useState(
    profile?.guardianConsentRequest?.email ?? profile?.guardianConsent?.email ?? '',
  )
  const [savingProfile, setSavingProfile] = useState(false)
  const idToken = useAuthStore((s) => s.idToken)

  // plan v1.3 G5 — mount 시 localStorage draft 확인. 있으면 DraftResumeBanner 노출.
  useEffect(() => {
    if (hasDraft()) setDraftToResume(loadDraft())
  }, [])

  useEffect(() => {
    setBirthDate(profile?.birthDate ?? '')
    setGrade(profile?.grade ?? '')
    setSchool(profile?.school ?? '')
    setGuardianEmail(
      profile?.guardianConsentRequest?.email ?? profile?.guardianConsent?.email ?? '',
    )
  }, [profile])

  useEffect(() => {
    const isDev = import.meta.env.DEV
    if (!idToken && !isDev) return
    let cancelled = false
    ;(async () => {
      try {
        const res = await apiFetch<SeedMetaResponse>('/api/projects/seeds')
        if (cancelled) return
        const next = {} as Record<CareerCategory, RecProject[]>
        for (const c of CATEGORY_OPTIONS) next[c.id] = []
        for (const seed of res.items) {
          next[seed.category].push({
            id: seed.seedId,
            title: seed.title,
            desc: seed.desc,
            se: seed.se,
            icon: seed.icon,
            isCached: seed.isCached,
            isAvailable: seed.isAvailable,
          })
        }
        setSeedMap(next)
      } catch (err) {
        console.warn('onboarding.seed_meta_failed', err)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [idToken])

  // 진입 시점에 본인 프로젝트 목록 fetch → ghost 후보가 1개 이상이고 sessionStorage 에 keep 표시
  // 없으면 모달 노출. 자동 삭제 X (plan §7.2 사용자 결정).
  // idToken (또는 dev mode) 준비될 때까지 대기 — production 에서 401 race 회피.
  useEffect(() => {
    const isDev = import.meta.env.DEV
    if (!idToken && !isDev) return
    if (needsAgeProfile(profile) || guardianBlocked(profile)) return
    let cancelled = false
    ;(async () => {
      if (sessionStorage.getItem(GHOST_KEEP_KEY) === '1') return
      try {
        const list = await apiFetch<{
          items: Array<VibeProject & { phase?: string }>
          nextPageToken: string | null
        }>('/api/projects')
        if (cancelled) return
        const found = (list.items ?? [])
          .filter(isGhostCandidate)
          .map((p) => ({ projectId: p.projectId, title: p.title }))
        if (found.length > 0) {
          setGhosts(found)
          setGhostOpen(true)
        }
      } catch (err) {
        if (!(err instanceof ApiClientError && err.status === 401)) {
          console.warn('onboarding.ghost_check_failed', err)
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [idToken, profile])

  const handleGhostDelete = async () => {
    const targets = ghosts
    // 각 DELETE 에 10s 타임아웃 — Worker stall 로 모달이 영구 freeze 되는 회귀 방지.
    const deleteOne = async (id: string) => {
      const ac = new AbortController()
      const timer = setTimeout(() => ac.abort(), 10_000)
      try {
        await apiFetch(`/api/projects/${id}`, { method: 'DELETE', signal: ac.signal })
      } finally {
        clearTimeout(timer)
      }
    }
    const results = await Promise.allSettled(targets.map((g) => deleteOne(g.projectId)))
    const failedItems = results
      .map((r, i) => (r.status === 'rejected' ? targets[i] : null))
      .filter((g): g is GhostProject => g !== null)
    const succeeded = targets.length - failedItems.length
    if (failedItems.length === 0) {
      toast.success(`${targets.length}개 작품을 정리했어요`)
      setGhosts([])
      setGhostOpen(false)
    } else {
      // 부분 실패 — 실패한 작품들만 모달에 남겨 학생이 재시도 또는 keep 선택할 수 있게.
      toast.error(`${succeeded}개 정리 완료, ${failedItems.length}개 실패 — 다시 시도해주세요`)
      setGhosts(failedItems)
    }
  }

  const handleGhostKeep = () => {
    sessionStorage.setItem(GHOST_KEEP_KEY, '1')
    setGhostOpen(false)
  }

  const nickname =
    profile?.displayName ||
    firebaseUser?.displayName?.split(' ')[0] ||
    firebaseUser?.email?.split('@')[0] ||
    '친구'

  const recommended: RecProject[] = category ? (seedMap ?? PROJECTS_BY_CATEGORY)[category] : []

  function toggleInterest(k: string) {
    setInterests((prev) =>
      prev.includes(k) ? prev.filter((x) => x !== k) : prev.length >= 5 ? prev : [...prev, k],
    )
  }

  function pickCategory(c: CareerCategory) {
    setCategory(c)
    setStep(2)
  }

  async function handleProjectPick(p: RecProject) {
    if (!category) return
    if (needsAgeProfile(profile) || guardianBlocked(profile)) {
      toast.error('학생 정보를 먼저 확인해 주세요')
      return
    }
    if (!seedAvailable(p)) {
      toast.info('이 작품은 곧 열릴 예정이에요', {
        description: '파일럿에서는 검증된 학습 시나리오부터 차례대로 열어요.',
      })
      return
    }
    setChosen(p.id)
    setBusy(true)
    try {
      // 1) onboarding 결과 저장 + 로컬 profile 갱신.
      await apiFetch('/api/auth/onboarding', {
        method: 'POST',
        body: JSON.stringify({ careerInterests: [category] }),
      }).catch(() => null)
      if (profile) {
        setProfile({ ...profile, careerInterests: [category], updatedAt: Date.now() })
      }

      // plan v1.3 G1 — Firestore doc 즉시 생성 X. localStorage draft 에만 보관.
      // PlanCards onConfirm 시점에 commit-from-draft 가 atomic 생성. ghost 누적 방지.
      // v2 (2026-07-20): newDraft 로 매번 새 draft — 같은 시드도 개별 진행·저장.
      newDraft({
        seedId: p.id,
        title: p.title,
        desc: p.desc,
        se: p.se,
        icon: p.icon,
        level: 'starter',
        template: 'react-vite',
        messages: [],
        savedAt: Date.now(),
      })

      // IDE store 미리 채워두기 — sidebar 제목 즉시 표시.
      setProjectInfo({
        projectId: '',
        title: p.title,
        level: 'starter',
        template: 'react-vite',
      })

      navigate({ to: '/plan', search: { seed: p.id } })
    } catch (err) {
      console.error('onboarding.project_pick_failed', err)
      toast.error('잠깐, 작품을 준비하다가 막혔어요. 잠시 후 다시 눌러봐요.')
      setChosen(null)
    } finally {
      setBusy(false)
    }
  }

  const handleResumeDraft = (d: DraftPayload) => {
    navigate({ to: '/plan', search: { seed: d.seedId } })
  }

  const handleDismissDraft = () => {
    clearDraft()
    setDraftToResume(null)
  }

  function handleSend() {
    if (step === 0) {
      if (interests.length === 0) return
      setStep(1)
    }
    setInput('')
  }

  const turns: Array<{ role: 'ai' | 'user'; text: string }> = [
    {
      role: 'ai',
      text: `안녕하세요, 만나서 반가워요.\n5분만 같이 얘기해보면서, ${nickname}한테 잘 맞을 프로젝트를 찾아봐요.\n요즘 관심 있는 분야가 뭐예요? (여러 개 골라도 돼요)`,
    },
  ]
  if (step >= 1) {
    turns.push({ role: 'user', text: interests.map((i) => '#' + i).join(' ') })
    turns.push({
      role: 'ai',
      text: `${interests.slice(0, 2).join('·')} 쪽에 관심 있네요. 진로로는 어디에 가까울까요? 아래에서 골라봐요.`,
    })
  }
  if (step >= 2 && category) {
    turns.push({ role: 'user', text: CATEGORY_DISPLAY[category] })
    turns.push({
      role: 'ai',
      text: `${CATEGORY_DISPLAY[category]} 쪽이네요. 잘 어울리는 프로젝트들을 골라봤어요.\n한번 볼래요?`,
    })
  }

  const progress = Math.min((step + 1) * 25, 100)

  // plan v1.3 §11.C — 학습 단계 3개 완료한 학생에게 "1차 학습 완료" 안내. 4번째 작품부터
  // 본격 커스텀 (§12) 흐름. 카운터는 agent loop loop_done 시 backend 가 증분.
  const completedExamples = profile?.completedExamples ?? 0
  const firstLearningDone = completedExamples >= 3
  const selectedAgeGroup = deriveAgeGroup(birthDate)

  async function handleProfileSubmit() {
    if (selectedAgeGroup === 'unknown') {
      toast.error('생년월일을 정확히 입력해 주세요')
      return
    }
    if (selectedAgeGroup === 'under14' && !guardianEmail.trim()) {
      toast.error('보호자 이메일을 입력해 주세요')
      return
    }
    setSavingProfile(true)
    try {
      await apiFetch('/api/auth/onboarding', {
        method: 'POST',
        body: JSON.stringify({
          birthDate,
          ageGroup: selectedAgeGroup,
          grade,
          school,
          guardianEmail: guardianEmail.trim(),
        }),
      })
      const fresh = await apiFetch<VibeUser>('/api/auth/me')
      setProfile(fresh)
      if (selectedAgeGroup === 'under14') {
        toast.info('보호자 확인이 필요해요', {
          description: '확인 전에는 작품 만들기와 AI 실행을 잠시 막아둘게요.',
        })
      } else {
        toast.success('학생 정보가 저장됐어요')
      }
    } catch (err) {
      toast.error('학생 정보 저장에 실패했어요', {
        description: err instanceof ApiClientError ? err.message : undefined,
      })
    } finally {
      setSavingProfile(false)
    }
  }

  if (authStatus === 'loading' || authStatus === 'signingIn') {
    return (
      <div className="bg-bg grid min-h-[calc(100vh-56px)] place-items-center px-5">
        <div className="text-text-2 text-[14px] font-semibold">학생 정보를 확인하고 있어요…</div>
      </div>
    )
  }

  if (needsAgeProfile(profile) || guardianBlocked(profile)) {
    const blocked = guardianBlocked(profile)
    return (
      <div className="bg-bg grid min-h-[calc(100vh-56px)] place-items-center px-5">
        <div className="border-border w-full max-w-[480px] rounded-2xl border bg-white p-6 shadow-sm">
          <div className="flex items-center gap-2">
            <BrandMark size={28} />
            <div>
              <h1 className="text-text text-[20px] font-extrabold tracking-tight">
                학생 정보를 먼저 확인할게요
              </h1>
              <p className="text-text-2 mt-1 text-[13px] leading-relaxed">
                만 14세 미만 보호자 동의는 꼭 필요한 절차예요.
              </p>
            </div>
          </div>

          <div className="mt-6 space-y-4">
            <label className="block">
              <span className="text-text-2 text-[12px] font-bold">생년월일</span>
              <input
                type="date"
                value={birthDate}
                onChange={(e) => setBirthDate(e.target.value)}
                className="border-border-strong focus:border-primary mt-1 w-full rounded-xl border px-3 py-3 text-[14px] outline-none"
              />
            </label>
            <label className="block">
              <span className="text-text-2 text-[12px] font-bold">학년</span>
              <input
                value={grade}
                onChange={(e) => setGrade(e.target.value)}
                placeholder="예: 고1"
                className="border-border-strong focus:border-primary mt-1 w-full rounded-xl border px-3 py-3 text-[14px] outline-none"
              />
            </label>
            <label className="block">
              <span className="text-text-2 text-[12px] font-bold">학교명 선택</span>
              <input
                value={school}
                onChange={(e) => setSchool(e.target.value)}
                placeholder="선택 입력"
                className="border-border-strong focus:border-primary mt-1 w-full rounded-xl border px-3 py-3 text-[14px] outline-none"
              />
            </label>

            {(selectedAgeGroup === 'under14' || blocked) && (
              <label className="bg-primary-soft block rounded-xl p-4">
                <span className="text-primary text-[12px] font-bold">보호자 이메일</span>
                <input
                  type="email"
                  value={guardianEmail}
                  onChange={(e) => setGuardianEmail(e.target.value)}
                  placeholder="parent@example.com"
                  className="border-border-strong focus:border-primary mt-2 w-full rounded-lg border bg-white px-3 py-3 text-[14px] outline-none"
                />
                <p className="text-text-2 mt-2 text-[12px] leading-relaxed">
                  확인 전에는 작품 만들기, AI 실행, 배포를 열지 않아요. 파일럿 중에는 운영자가 서면
                  동의 확인 후 계정을 열 수 있어요.
                </p>
              </label>
            )}
          </div>

          <button
            onClick={handleProfileSubmit}
            disabled={savingProfile}
            className="bg-primary hover:bg-primary-hover mt-6 w-full rounded-xl py-3 text-[14px] font-bold text-white disabled:opacity-50"
          >
            {savingProfile
              ? '저장 중…'
              : blocked
                ? '보호자 이메일 다시 저장하기'
                : '저장하고 시작하기'}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-bg flex h-[calc(100vh-56px)] flex-col">
      {firstLearningDone && (
        <div className="bg-primary-soft border-primary/20 text-text flex items-center gap-3 border-b px-5 py-3 text-[13px]">
          <span aria-hidden>🎓</span>
          <div className="leading-relaxed">
            <span className="text-primary font-extrabold">여기까지 1차 학습 완료!</span>
            <span className="text-text-2 ml-2">
              이제부터는 본인이 좋아하는 웹사이트나 캐릭터 디자인을 추가하거나, 진짜 새 기능을
              처음부터 만들어볼 수 있어요. 토큰 소모량은 더 많아지지만, 그게 진짜 바이브 코딩이에요.
            </span>
          </div>
        </div>
      )}
      {draftToResume && (
        <DraftResumeBanner
          draft={draftToResume}
          onContinue={handleResumeDraft}
          onDismiss={handleDismissDraft}
        />
      )}
      <GhostCleanupModal
        open={ghostOpen}
        // ESC / backdrop / X 버튼은 단순 닫기만 — sessionStorage 마킹은 명시 "그대로 둘게요" 버튼에만.
        // 학생이 다음 onboarding 진입에서 다시 묻고 싶을 수도 있어 우발적 dismiss 를 의향으로 해석 X.
        onOpenChange={(v) => {
          if (!v) setGhostOpen(false)
        }}
        ghosts={ghosts}
        onConfirmDelete={handleGhostDelete}
        onKeep={handleGhostKeep}
      />
      <header className="border-border flex items-center gap-3 border-b bg-white px-5 py-3">
        <div className="flex items-center gap-2">
          <BrandMark size={26} />
          <span className="text-text text-[15px] font-extrabold tracking-tight">환영</span>
          <span className="text-text-3 text-[12px]">· 입학 인터뷰</span>
        </div>
        <div className="text-text-2 ml-auto flex items-center gap-3 text-[12px]">
          <span className="font-semibold">관심사 {Math.min(step + 1, 4)}/4</span>
          <div className="bg-surface-alt h-1 w-32 overflow-hidden rounded-full">
            <div
              className="bg-primary h-full rounded-full transition-[width] duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto">
        <div className="chat-thread">
          {turns.map((t, i) => (
            <div key={i} className={`msg ${t.role}`}>
              <div className="av">{t.role === 'ai' ? 'V' : nickname.slice(0, 1)}</div>
              <div className="bubble2 whitespace-pre-wrap">{t.text}</div>
            </div>
          ))}

          {step === 0 && (
            <div className="ml-[38px] flex flex-wrap gap-1.5">
              {INTEREST_KEYWORDS.map((k) => (
                <button
                  key={k}
                  className={`chip ${interests.includes(k) ? 'selected' : ''}`}
                  onClick={() => toggleInterest(k)}
                >
                  #{k}
                </button>
              ))}
            </div>
          )}

          {step === 1 && (
            <div className="ml-[38px] flex flex-wrap gap-1.5">
              {CATEGORY_OPTIONS.map((c) => (
                <button
                  key={c.id}
                  className={`chip ${category === c.id ? 'selected' : ''}`}
                  onClick={() => pickCategory(c.id)}
                >
                  {c.label}
                </button>
              ))}
            </div>
          )}

          {step >= 2 && category && (
            <div className="ml-[38px] flex flex-col gap-2.5">
              {recommended.map((p) => (
                <button
                  key={p.id}
                  className={`rec-card text-left ${seedAvailable(p) ? '' : 'opacity-60'}`}
                  onClick={() => handleProjectPick(p)}
                  disabled={busy}
                  style={
                    chosen === p.id
                      ? {
                          borderColor: 'var(--color-primary)',
                          boxShadow: '0 0 0 3px var(--color-primary-soft)',
                        }
                      : undefined
                  }
                >
                  <div className="rec-thumb">
                    <span className="text-[22px]">{p.icon}</span>
                  </div>
                  <div className="rec-body">
                    <h5>{p.title}</h5>
                    <p>{p.desc}</p>
                    <div className="se">✨ {p.se}</div>
                    {!seedAvailable(p) && (
                      <span className="bg-surface-alt text-text-3 mt-2 self-start rounded-md px-3 py-1.5 text-[12px] font-bold">
                        준비 중
                      </span>
                    )}
                    <span className="bg-primary mt-2 self-start rounded-md px-3 py-1.5 text-[12px] font-bold text-white">
                      {chosen === p.id && busy
                        ? '준비하는 중…'
                        : seedAvailable(p)
                          ? '이걸로 시작할래요'
                          : '곧 열릴 예정'}
                    </span>
                  </div>
                </button>
              ))}
              <div
                className="bg-accent-soft mt-2 rounded-xl p-3 text-[12px]"
                style={{ color: '#7A5A00' }}
              >
                <b>처음 한 번은 무료예요.</b> 부담 없이 하나 골라서 만들어봐요.
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="border-border border-t bg-white px-5 py-4">
        <div className="border-border bg-surface-alt mx-auto flex max-w-[720px] items-end gap-2 rounded-xl border p-2">
          <textarea
            rows={1}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                e.preventDefault()
                handleSend()
              }
            }}
            placeholder={
              step === 0
                ? '관심사를 골라봐요'
                : step === 1
                  ? '아래에서 진로를 골라봐요'
                  : '편하게 적어주세요'
            }
            className="placeholder:text-text-3 flex-1 resize-none bg-transparent px-2 py-2 text-[14px] outline-none"
          />
          <button
            onClick={handleSend}
            disabled={step !== 0 || interests.length === 0}
            className="bg-primary hover:bg-primary-hover rounded-lg px-3 py-2 text-[12px] font-bold text-white disabled:opacity-50"
          >
            보내기
          </button>
        </div>
        <div className="text-text-3 mx-auto mt-2 flex max-w-[720px] items-center gap-1.5 text-[11px]">
          <span className="kbd">Ctrl</span> + <span className="kbd">Enter</span> 전송
          <span className="ml-auto">바이브타운 챗봇</span>
        </div>
      </div>
    </div>
  )
}
